/**
 * Slang WASM Compiler Wrapper
 */

export class SlangCompiler {
  constructor() {
    this.module = null;
    this.globalSession = null;
    this.compileTargetMap = null;
    this.isReady = false;
  }

  /**
   * Initialize the Slang WASM module
   * @returns {Promise<boolean>}
   */
  async init() {
    try {
      // Dynamic import of the WASM module
      const createModule = (await import('../wasm/slang-wasm.js')).default;

      // Initialize the module with locateFile to find the .wasm file
      this.module = await createModule({
        locateFile: (path) => {
          if (path.endsWith('.wasm')) {
            return new URL('../wasm/slang-wasm.wasm', import.meta.url).href;
          }
          return path;
        },
        // Suppress some Emscripten warnings
        noInitialRun: true,
        noExitRuntime: true,
      });

      // Create global session
      this.globalSession = this.module.createGlobalSession();
      if (!this.globalSession) {
        throw new Error('Failed to create global session');
      }

      // Get compile targets dynamically
      this.compileTargetMap = this.module.getCompileTargets();

      this.isReady = true;
      console.log('Slang compiler initialized:', this.module.getVersionString());
      return true;
    } catch (error) {
      console.error('Failed to initialize Slang compiler:', error);
      return false;
    }
  }

  /**
   * Find compile target ID by name
   * @param {string} targetName
   * @returns {number|null}
   */
  findCompileTarget(targetName) {
    if (!this.compileTargetMap) return null;
    const target = this.compileTargetMap.find(
      (t) => t.name.toLowerCase() === targetName.toLowerCase()
    );
    return target ? target.value : null;
  }

  /**
   * Get available compile targets
   * @returns {object}
   */
  getCompileTargets() {
    if (!this.isReady) return null;
    return this.module.getCompileTargets();
  }

  /**
   * Compile Slang source code to WGSL
   * @param {string} source - Slang source code
   * @param {string} [moduleName='shader'] - Module name
   * @param {string} [targetName='WGSL'] - Compile target name
   * @returns {{success: boolean, code?: string, diagnostics?: string, error?: string}}
   */
  compile(source, moduleName = 'shader', targetName = 'WGSL') {
    if (!this.isReady) {
      return { success: false, error: 'Compiler not initialized' };
    }

    // Find target ID
    const compileTarget = this.findCompileTarget(targetName);
    if (compileTarget === null) {
      const available = (this.compileTargetMap || []).map((t) => t.name).join(', ');
      return {
        success: false,
        error: `Unknown compile target: ${targetName}. Available: ${available}`,
      };
    }

    let session = null;
    let slangModule = null;
    let linkedProgram = null;

    try {
      // Create session with target
      session = this.globalSession.createSession(compileTarget);
      if (!session) {
        const lastError = this.module.getLastError();
        return {
          success: false,
          error: `Failed to create session: ${lastError?.message || 'Unknown error'}`,
        };
      }

      // Load module from source
      const modulePath = `/${moduleName}.slang`;
      slangModule = session.loadModuleFromSource(source, moduleName, modulePath);

      if (!slangModule) {
        const lastError = this.module.getLastError();
        const errorMessage = lastError?.message || 'Failed to load module';
        return {
          success: false,
          error: errorMessage,
          diagnostics: this.formatDiagnostics(lastError),
          errorLines: this.parseErrorLines(errorMessage),
        };
      }

      // Find entry points and link
      const entryPointCount = slangModule.getDefinedEntryPointCount();
      if (entryPointCount === 0) {
        return {
          success: false,
          error: 'No entry points found. Add [shader("vertex")], [shader("fragment")], or [shader("compute")] attribute to your entry point functions.',
        };
      }

      // Create component type array for linking
      const components = [slangModule];
      for (let i = 0; i < entryPointCount; i++) {
        const entryPoint = slangModule.getDefinedEntryPoint(i);
        if (entryPoint) {
          components.push(entryPoint);
        }
      }

      // Create composite and link
      const composite = session.createCompositeComponentType(components);
      if (!composite) {
        const lastError = this.module.getLastError();
        return {
          success: false,
          error: `Failed to create composite: ${lastError?.message || 'Unknown error'}`,
        };
      }

      linkedProgram = composite.link();
      if (!linkedProgram) {
        const lastError = this.module.getLastError();
        return {
          success: false,
          error: `Link failed: ${lastError?.message || 'Unknown error'}`,
        };
      }

      // Get WGSL code
      const wgslCode = linkedProgram.getTargetCode(0);

      return {
        success: true,
        code: wgslCode,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Compilation failed',
      };
    } finally {
      // Cleanup
      if (linkedProgram) linkedProgram.delete();
      if (slangModule) slangModule.delete();
      if (session) session.delete();
    }
  }

  /**
   * Format error diagnostics for display
   * @param {object} error
   * @returns {string}
   */
  formatDiagnostics(error) {
    if (!error) return '';

    let output = '';
    if (error.message) {
      output = error.message;
    }
    return output;
  }

  /**
   * Parse error message to extract line numbers
   * @param {string} message
   * @returns {number[]} Array of line numbers (1-indexed)
   */
  parseErrorLines(message) {
    if (!message) return [];

    const lines = [];
    // Match patterns like "/shader.slang(10):" or "shader.slang:10:"
    const regex = /\.slang[:(](\d+)[):]?/g;
    let match;
    while ((match = regex.exec(message)) !== null) {
      const lineNum = parseInt(match[1], 10);
      if (!lines.includes(lineNum)) {
        lines.push(lineNum);
      }
    }
    return lines;
  }

  /**
   * Get compiler version
   * @returns {string}
   */
  getVersion() {
    if (!this.isReady) return 'Not initialized';
    return this.module.getVersionString();
  }

  /**
   * Cleanup resources
   */
  destroy() {
    if (this.globalSession) {
      this.globalSession.delete();
      this.globalSession = null;
    }
    this.module = null;
    this.isReady = false;
  }
}

// Singleton instance
let compilerInstance = null;

/**
 * Get or create the compiler instance
 * @returns {Promise<SlangCompiler>}
 * @throws {Error} If compiler initialization fails
 */
export async function getCompiler() {
  if (!compilerInstance) {
    const compiler = new SlangCompiler();
    const success = await compiler.init();
    if (!success) {
      throw new Error('Compiler initialization failed');
    }
    compilerInstance = compiler;
  }
  return compilerInstance;
}

/**
 * Reset the compiler instance (useful for retry after failure)
 */
export function resetCompiler() {
  if (compilerInstance) {
    compilerInstance.destroy();
    compilerInstance = null;
  }
}
